import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import './Auth.scss';

const Callback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const [status, setStatus] = useState('checking');
  const [message, setMessage] = useState('');
  const [countdown, setCountdown] = useState(5);
  const [paymentType, setPaymentType] = useState('');
  const timerRef = useRef(null);
  const countdownRef = useRef(null);
  const mountedRef = useRef(true);

  const token = searchParams.get('token') || searchParams.get('tokenPay');

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      clearTimeout(timerRef.current);
      clearInterval(countdownRef.current);
    };
  }, []);

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMessage('Token de paiement manquant.');
      return;
    }

    const checkStatus = async () => {
      try {
        const { data } = await api.get(`/payment/status/${token}`);

        if (!mountedRef.current) return;

        if (data.status === 'paid') {
          setPaymentType(data.type);
          setStatus('success');
          if (user) {
            updateUser({ is_paid: true });
          }
          setMessage(data.type === 'registration'
            ? 'Votre cotisation a été payée avec succès ! Votre inscription est maintenant validée.'
            : 'Votre don a été effectué avec succès ! Merci pour votre contribution.'
          );
          let count = 5;
          countdownRef.current = setInterval(() => {
            count -= 1;
            if (!mountedRef.current) { clearInterval(countdownRef.current); return; }
            setCountdown(count);
            if (count <= 0) {
              clearInterval(countdownRef.current);
              if (data.type === 'registration') {
                navigate('/dashboard');
              } else {
                navigate('/');
              }
            }
          }, 1000);
        } else if (data.status === 'failure' || data.status === 'no paid' || data.status === 'cancelled') {
          setStatus('failed');
          setMessage('Le paiement a échoué ou a été annulé. Veuillez réessayer.');
        } else {
          setStatus('pending');
          setMessage('Paiement en cours de traitement. Veuillez patienter...');
          timerRef.current = setTimeout(checkStatus, 5000);
        }
      } catch (err) {
        if (!mountedRef.current) return;
        setStatus('error');
        setMessage(err.response?.data?.message || 'Erreur lors de la vérification du paiement.');
      }
    };

    checkStatus();
  }, [token]);

  const handleRedirect = () => {
    if (status === 'success') {
      if (user) {
        navigate('/dashboard');
      } else {
        navigate('/connexion');
      }
    } else if (status === 'failed' || status === 'error') {
      if (user && !user.is_paid) {
        navigate('/inscription');
      } else {
        navigate('/faire-un-don');
      }
    }
  };

  return (
    <div className="auth-page">
      <PageHeader
        title="Confirmation du paiement"
        subtitle="Vérification de votre transaction"
      />
      <div className="auth-container">
        <div className="auth-card" style={{ maxWidth: '600px', margin: '0 auto', textAlign: 'center' }}>
          {status === 'checking' && (
            <>
              <div style={{ fontSize: '48px', marginBottom: '20px' }}>⏳</div>
              <h2>Vérification en cours...</h2>
              <p style={{ color: '#888', marginTop: '10px' }}>
                Nous vérifions le statut de votre paiement. Veuillez patienter.
              </p>
            </>
          )}

          {status === 'pending' && (
            <>
              <div style={{ fontSize: '48px', marginBottom: '20px' }}>⏳</div>
              <h2>Paiement en cours</h2>
              <p style={{ color: '#888', marginTop: '10px' }}>{message}</p>
              <p style={{ color: '#aaa', fontSize: '13px', marginTop: '10px' }}>
                Ne fermez pas cette page, la vérification se fait automatiquement.
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <div style={{ fontSize: '48px', marginBottom: '20px' }}>✅</div>
              <h2 style={{ color: '#28a745' }}>Paiement réussi !</h2>
              <p style={{ color: '#555', marginTop: '15px', fontSize: '15px' }}>{message}</p>
              <p style={{ color: '#aaa', fontSize: '13px', marginTop: '10px' }}>
                Redirection automatique dans <strong>{countdown}</strong> seconde{countdown !== 1 ? 's' : ''}...
              </p>
              <button className="auth-submit-btn" onClick={handleRedirect} style={{ marginTop: '15px' }}>
                {paymentType === 'registration' ? 'Accéder à mon espace maintenant' : 'Retour à l\'accueil'}
              </button>
            </>
          )}

          {status === 'failed' && (
            <>
              <div style={{ fontSize: '48px', marginBottom: '20px' }}>❌</div>
              <h2 style={{ color: '#dc3545' }}>Paiement échoué</h2>
              <p style={{ color: '#555', marginTop: '15px', fontSize: '15px' }}>{message}</p>
              <button className="auth-submit-btn" onClick={handleRedirect} style={{ marginTop: '20px' }}>
                Réessayer
              </button>
            </>
          )}

          {status === 'error' && (
            <>
              <div style={{ fontSize: '48px', marginBottom: '20px' }}>⚠️</div>
              <h2 style={{ color: '#dc3545' }}>Erreur</h2>
              <p style={{ color: '#555', marginTop: '15px', fontSize: '15px' }}>{message}</p>
              <button className="auth-submit-btn" onClick={() => navigate('/')} style={{ marginTop: '20px' }}>
                Retour à l'accueil
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Callback;
